import { CommonDialogs } from "@mcleod/common";
import { AutogenLayoutLeaveHistory } from "./autogen/AutogenLayoutLeaveHistory";
import {
    DataSourceAction, DataSourceExecutionEvent, DataSourceModeChangeEvent, DataSourceMode,
    DropdownItem, LookupModelSearchEvent, ChangeEvent, Layout, SaveButton, SlideoutDecorator
} from "@mcleod/components";
import { Api, DateUtil, ModelRow } from "@mcleod/core";
import { OfficeLeaveHistory } from "./officepayroll/OfficeLeaveHistory";
import { SettlementLeaveHistory } from "./SettlementLeaveHistory";

export interface LeaveSlideoutProps {
    leaveRow?: ModelRow;
    onClose?: (row: ModelRow, balanceRow: ModelRow, postMade: boolean) => void;
    mode: DataSourceMode;
}

export interface LeaveHistoryProps {
    layout: OfficeLeaveHistory | SettlementLeaveHistory
    beforePayeeLookup: (event: LookupModelSearchEvent) => void;
}

export interface LeaveHistoryState {
    postMade: boolean;
    balanceRow: ModelRow;

}

export class LeaveHistory  {
    private layout : OfficeLeaveHistory | SettlementLeaveHistory;
    constructor(props: LeaveHistoryProps) {
        this.layout = props.layout;
        this.layout.mainDataSource.doBeforeDataSourcePost = async () => {
            // if these required fields are not set return true and let the layout handle the error
            // Not sure if this is the best way to handle this
            const activeRow = this.layout.activeRow;
            if(activeRow.isNull("payee_id") || activeRow.isNull("applies_to") || activeRow.isNull("amount")) {
                return true;
            }

            if (!this.layout.textboxTrxDate.visible || activeRow.isNull("trx_date")) {
                activeRow.set("trx_date", activeRow.getDate("check_date"));
            }

            const response = await Api.post("lme/settlement/most-recent-leave-balance-date", {
                    payee_id: activeRow.get("payee_id"),
                    applies_to: activeRow.get("applies_to"),
                    update: this.layout.mainDataSource.mode === DataSourceMode.UPDATE,
                });
                if (response.data && response.data.length > 0) {
                    const date = response.data[0]?.most_recent_date;
                    if (date) {
                        const recentDate = DateUtil.parseDate(date);
                        const accrualDate = activeRow.getDate("trx_date");
                        if (accrualDate < recentDate) {
                            CommonDialogs.showMessage(`Date cannot be before the most recent Balance record ${DateUtil.formatDate(recentDate)}.`);
                            return false;
                        }
                    }
                }

            return true;
        }
        this.layout.mainDataSource.addAfterExecutionListener((event: DataSourceExecutionEvent) => {
            if (event.getAction() === DataSourceAction.SEARCH) {
                const canEdit = props.layout.activeRow.getBoolean("can_edit", false);
                props.layout.textboxTrxDate.enabled = canEdit;
                if (!canEdit) {
                    props.layout.titleEditModeSuffix = " ";
                }
            }
        });
        this.layout.switchType.addChangeListener((event: ChangeEvent) => {
            if (event.newValue) {
                this.layout.textboxMemo.text = "Beginning balance adjustment"
            } else {
                this.layout.textboxMemo.text = "Adjustment to balance";
            }
            this.layout.activeRow.set("is_beginning_balance", event.newValue);
            this.layout.textboxTrxDate.visibleDuringAdd = event.newValue;
            this.layout.textboxTrxDate.visible = event.newValue;
            this.layout.textboxTrxDate.required = event.newValue;
        });
        this.layout.mainDataSource.addAfterModeChangeListener((event: DataSourceModeChangeEvent) => {
            if(event.newMode == DataSourceMode.ADD) {
                this.setupAdd();
            }
        });
        this.layout.textboxPayeeId.addBeforeLookupModelSearchListener((event: LookupModelSearchEvent) => {
            props.beforePayeeLookup(event);
        });


    }


    setupAdd(): void {
        this.layout.textboxCheckDate.caption = "Date";
        this.layout.panelBatch.visible = false;
        this.layout.activeRow.set("check_date", new Date());
        this.layout.textboxEffect.items = [new DropdownItem("X", "Adjustment")];
        this.layout.textboxEffect.selectedItem = this.layout.textboxEffect.items[0];
        this.layout.switchType.visible = true;
    }

    static createSlideout(layout: SettlementLeaveHistory | OfficeLeaveHistory, props: LeaveSlideoutProps): void {
        const saveButton = SaveButton.create({
            caption: "Save & Close",
            rowBreak: false,
            enabled: false
        });
        new SlideoutDecorator({
            layout:layout,
            title:"Leave History",
            fillHeight: true,
            addlComponents: saveButton,
            layoutLoadListeners: async (event) => {
                if (props.mode === DataSourceMode.ADD) {
                    layout.mainDataSource.setRowsAndMode(DataSourceMode.ADD, [await layout.mainDataSource.createBlankRow()]);
                    const textboxPayee = layout.textboxPayeeId;
                    if (props.leaveRow) {
                        const leaveRow = props.leaveRow;
                        textboxPayee.enabled = false;
                        const lookupModelRow = new ModelRow("");
                        lookupModelRow.set("id", leaveRow.get("id"));
                        lookupModelRow.set("name", leaveRow.get("name"));
                        layout.activeRow.setLookupModelData("payee_id", lookupModelRow);
                        layout.activeRow.set("payee_id", leaveRow.get("id"));
                    }
                }
                else {
                    layout.mainDataSource.search({id: props.leaveRow.get("id")});
                    layout.mainDataSource.mode = props.mode;
                }

            },
            doAfterSlideIn: async (decorator: SlideoutDecorator) => {
                layout.mainDataSource.addHasChangedComponent(saveButton);
                saveButton.addClickListener(async (event) => {
                    if (decorator.validateSimple()) {
                        saveButton.busy = true;
                        layout.mainDataSource.post().then((response) => {
                            if(!response.isNull("balance_data")) {
                                layout.balanceRow = new ModelRow(layout.mainDataSource.url, false, response.get("balance_data"));
                            }
                            layout.postMade = true;
                            decorator.slideOut();
                        }).finally(() => {
                            saveButton.busy = false;

                        });
                    }
                });
            },
            doAfterSlideOut: (decorator: SlideoutDecorator) => {
                if (props.onClose) {
                    props.onClose(layout.activeRow, layout.balanceRow, layout.postMade);
                }
            }
        });
    }

    static showSettlementAsSlideout(props: LeaveSlideoutProps): void {
        const layout = Layout.getLayout("lme/settlement/SettlementLeaveHistory") as SettlementLeaveHistory;
        LeaveHistory.createSlideout(layout, props);
    }

    static showOfficeAsSlideout(props: LeaveSlideoutProps): void {
        const layout = Layout.getLayout("lme/settlement/officepayroll/OfficeLeaveHistory") as OfficeLeaveHistory;
        LeaveHistory.createSlideout(layout, props);
    }



}
